import { type Control, Controller, type UseFormRegister, useWatch } from 'react-hook-form'

import { ButtonGroup, YesNo } from '@/components/ui/button-group'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { ClaimCitationIssued } from '../../../../models/claim'
import type { Step4FormValues } from './claimStep4Values'

const CITATION_OPTIONS: { value: string; label: string }[] = [
  { value: ClaimCitationIssued.NONE, label: 'No one' },
  { value: ClaimCitationIssued.OUR_DRIVER, label: 'Our driver' },
  { value: ClaimCitationIssued.OTHER_DRIVER, label: 'Other driver' },
  { value: ClaimCitationIssued.UNKNOWN, label: 'Unknown' },
]

interface ClaimPoliceSectionProps {
  control: Control<Step4FormValues>
  register: UseFormRegister<Step4FormValues>
}

export function ClaimPoliceSection({ control, register }: ClaimPoliceSectionProps) {
  const contacted = useWatch({ control, name: 'policeContacted' })

  return (
    <>
      <Controller
        name="policeContacted"
        control={control}
        rules={{ validate: (v) => v !== null || 'Please choose.' }}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-neutral-700">Was police contacted? *</label>
            <YesNo value={field.value} onChange={field.onChange} />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      {contacted === true && (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Controller
              name="policeResponded"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-neutral-700">
                    Did police respond to the scene?
                  </label>
                  <YesNo value={field.value} onChange={field.onChange} />
                </div>
              )}
            />
            <Controller
              name="reportFiled"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-neutral-700">Was a report filed?</label>
                  <YesNo value={field.value} onChange={field.onChange} />
                </div>
              )}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-700">Report / case number</label>
              <Input placeholder="Leave blank if not yet available" {...register('reportCaseNumber')} />
              <p className="text-xs text-neutral-500">Can be added after submission</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-700">Jurisdiction / department</label>
              <Input placeholder="e.g. CHP, Fresno PD, County Sheriff" {...register('jurisdiction')} />
            </div>
          </div>
        </>
      )}

      <Controller
        name="citationIssued"
        control={control}
        rules={{ validate: (v) => !!v || 'Please choose.' }}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2 border-t pt-5">
            <label className="text-sm font-medium text-neutral-700">Was a citation issued? *</label>
            <ButtonGroup options={CITATION_OPTIONS} value={field.value} onChange={field.onChange} />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />
    </>
  )
}
